import { useCallback, useState } from "react";
import { useMutation } from "@apollo/client";
import BULK_REGISTER_VEHICLES, {
  BulkRegisterVehiclesData,
  BulkRegisterVehiclesVars,
  BulkRegisterVehicleInput,
} from "common/graphql/mutations/BulkRegisterVehicles";
import { NewVehicleRow } from "../vehicles.types";
import { LicensePlateState, StateAbbreviation, VehicleTypeValue } from "@app.automotus.io/components/common";

const BATCH_SIZE = 200;

export type SubmitStatus = "idle" | "submitting" | "complete" | "error";

export interface BulkSubmitResult {
  successCount: number;
  failedRows: NewVehicleRow[];
  networkError?: boolean;
}

interface UseBulkSubmitOptions {
  onComplete: (result: BulkSubmitResult) => void;
}

export interface UseBulkSubmitResult {
  submit: (rows: NewVehicleRow[]) => Promise<void>;
  status: SubmitStatus;
  progress: { current: number; total: number };
  reset: () => void;
}

export function toInput(row: NewVehicleRow): BulkRegisterVehicleInput {
  const countryCode =
    LicensePlateState.FromAbbreviation(row.stateCode as StateAbbreviation)?.countryCode === "CA" ? "CAN" : "USA";
  return {
    license_plate: row.licensePlate,
    state_code: row.stateCode,
    vehicle_type: row.type as VehicleTypeValue,
    vehicle_category: row.category,
    country_code: countryCode,
    make: row.make || undefined,
    model: row.model || undefined,
  };
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

export function useBulkSubmit({ onComplete }: UseBulkSubmitOptions): UseBulkSubmitResult {
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const [bulkRegister] = useMutation<BulkRegisterVehiclesData, BulkRegisterVehiclesVars>(BULK_REGISTER_VEHICLES);

  const submit = useCallback(
    async (rows: NewVehicleRow[]) => {
      const batches = chunkArray(rows, BATCH_SIZE);
      setStatus("submitting");
      setProgress({ current: 0, total: batches.length });

      const failedRows: NewVehicleRow[] = [];
      let successCount = 0;

      try {
        for (let batchIdx = 0; batchIdx < batches.length; batchIdx++) {
          const batch = batches[batchIdx];
          const { data } = await bulkRegister({ variables: { vehicles: batch.map(toInput) } });
          const results = data?.bulkRegisterVehicles.results ?? [];

          results.forEach((result) => {
            if (result.status === "success") {
              successCount++;
            } else {
              failedRows.push({ ...batch[result.index], submitErrorCode: result.errorCode });
            }
          });

          setProgress({ current: batchIdx + 1, total: batches.length });
        }

        setStatus("complete");
        onComplete({ successCount, failedRows });
      } catch {
        const processed = successCount + failedRows.length;
        const unsent = rows.slice(processed).map((r) => ({ ...r, submitErrorCode: "not_submitted" }));
        setStatus("error");
        onComplete({ successCount, failedRows: [...failedRows, ...unsent], networkError: true });
      }
    },
    [bulkRegister, onComplete],
  );

  const reset = useCallback(() => {
    setStatus("idle");
    setProgress({ current: 0, total: 0 });
  }, []);

  return { submit, status, progress, reset };
}

export default useBulkSubmit;
